import { FC, Fragment } from 'react';
import { SearchResult } from '@les-sherpas/sherpas-toolbox';
import { useMediaQuery } from '@mui/system';
import { nanoid } from 'nanoid';

import useAuthentication from '@/components/Authentication/useAuthentication';
import CardResultSkeleton from '@/components/Search/components/CardResultSkelton/CardResultSkeleton';
import SearchFloater from '@/components/Search/components/SearchFloater/SearchFloater';
import useShowHotLeadBanner from '@/components/Search/hooks/useShowHotLeadBanner';
import Loader from '@/Molecules/Loader/Loader/Loader';
import useNavigation, { urls } from '@/shared/Hooks/useNavigation';
import LoginSignUpModalWrapper from '@/shared/LoginSignupModalWrapper/LoginSignupModalWrapper';
import SearchResultCard from '@/shared/SearchResultCard/SearchResultCard';
import { getLocationOfInterest } from '@/utils/getLocationOfInterest/getLocationOfInterest';
import { getRedirectionUrlTo } from '@/utils/getRedirectionUrlTo/getRedirectionUrlTo';

import useStyles from './styles';

type Props = {
  isLoading: boolean;
  announces: SearchResult[];
  withHotLeadBanner?: boolean;
};

export const SearchResultGridItems: FC<Props> = ({
  announces,
  isLoading,
  withHotLeadBanner = true,
}) => {
  const {
    classes,
    theme: { breakpoints },
  } = useStyles();

  const { authentication } = useAuthentication();
  const { query } = useNavigation();

  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const isTablet = useMediaQuery(breakpoints.between('sm', 'lg'));

  const preferredIndex = () => {
    if (isMobile) {
      return 1;
    }
    if (isTablet) {
      return 2;
    }
    return 6;
  };

  const { show: showHotLeadBanner } = useShowHotLeadBanner({
    preferredIndex: preferredIndex(),
  });

  const locationOfInterest = getLocationOfInterest({
    queryParam: query.courseLocation,
  });

  const skeletons = new Array(6).fill(0);

  if (!authentication?.authData) {
    return <Loader />;
  }

  return (
    <div className={classes.container}>
      {announces?.map((searchResult, index) => {
        const redirectionUrlToDceFunnel = getRedirectionUrlTo({
          url: urls.dceFunnel({
            announceId: searchResult.id,
            subjectName: searchResult.subject,
            locationOfInterest,
          }),
        });

        return (
          <Fragment key={searchResult.id}>
            <LoginSignUpModalWrapper
              origin="announce"
              initialVariant="signup"
              drawerExitUrl={redirectionUrlToDceFunnel}
              teacher={searchResult.teacher}
              renderItem={({ toggle }) => (
                <SearchResultCard
                  searchResult={searchResult}
                  showLoginSignupModal={toggle}
                />
              )}
            />
            {withHotLeadBanner &&
              showHotLeadBanner(index, announces.length) && (
                <div
                  key={`hotlead-banner-${searchResult.id}`}
                  className={classes.banner}
                >
                  <LoginSignUpModalWrapper
                    origin="search"
                    initialVariant="signup"
                    drawerExitUrl={`${process.env.NEXT_PUBLIC_FRONT_URL}/student-need?originButton=search`}
                    renderItem={({ toggle }) => (
                      <SearchFloater showLoginSignupModal={toggle} />
                    )}
                  />
                </div>
              )}
          </Fragment>
        );
      })}
      {isLoading &&
        skeletons.map(() => (
          <div key={`skeleton-${nanoid()}`}>
            <CardResultSkeleton />
          </div>
        ))}
    </div>
  );
};
